'use client';

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

// --- Data (Copied from PricingTiers.tsx to ensure consistency) ---
const plans = {
    development: [
        {
            id: 'basic',
            name: 'Basic',
            price: '₹30,000',
            subtitle: 'Perfect for startups',
            desc: 'Essential web presence with modern design.',
            popular: false,
            gradient: 'from-slate-700 to-slate-900',
            btnStyle: 'border-2 border-slate-200 text-slate-700',
            icon: 'RocketLaunchIcon'
        },
        {
            id: 'pro',
            name: 'Professional',
            price: '₹80,000',
            subtitle: 'Most Popular',
            desc: 'Robust solution for growing businesses.',
            popular: true,
            gradient: 'from-indigo-600 to-violet-600',
            btnStyle: 'bg-indigo-600 text-white shadow-lg shadow-indigo-200',
            icon: 'BriefcaseIcon'
        },
        {
            id: 'ent',
            name: 'Enterprise',
            price: 'Contact Us',
            subtitle: 'Full Scale',
            desc: 'Complete digital transformation.',
            popular: false,
            gradient: 'from-slate-800 to-black',
            btnStyle: 'bg-slate-900 text-white',
            icon: 'BuildingOffice2Icon'
        }
    ],
    growth: [
        {
            id: 'seo',
            name: 'SEO Starter',
            price: '₹10,000',
            subtitle: 'Organic Growth',
            desc: 'Boost your rankings and local visibility.',
            popular: false,
            gradient: 'from-blue-600 to-cyan-600',
            btnStyle: 'border-2 border-blue-200 text-slate-700',
            icon: 'MagnifyingGlassIcon'
        },
        {
            id: 'gmb',
            name: 'GMB Management',
            price: '₹10,000',
            subtitle: 'Local Dominance',
            desc: 'Optimize your Google Business Profile.',
            popular: true,
            gradient: 'from-violet-600 to-fuchsia-600',
            btnStyle: 'bg-violet-600 text-white shadow-lg shadow-violet-200',
            icon: 'MapPinIcon'
        }
    ]
};

const featuresData = {
    development: [
        { name: 'Custom Design', basic: true, pro: true, ent: true },
        { name: 'Responsive Layout', basic: true, pro: true, ent: true },
        { name: 'SEO Foundation', basic: true, pro: true, ent: true },
        { name: 'CMS Integration', basic: false, pro: true, ent: true },
        { name: 'E-commerce', basic: false, pro: true, ent: true },
        { name: 'API Integration', basic: false, pro: false, ent: true },
        { name: 'Priority Support', basic: false, pro: true, ent: true },
    ],
    growth: [
        { name: 'Keyword Research', seo: true, gmb: false },
        { name: 'On-Page Optimization', seo: true, gmb: false },
        { name: 'Profile Setup & Verification', seo: false, gmb: true },
        { name: 'Review Management', seo: false, gmb: true },
        { name: 'Weekly Posting', seo: false, gmb: true },
        { name: 'Monthly Reporting', seo: true, gmb: true },
        { name: 'Competitor Analysis', seo: true, gmb: true },
    ]
};

const MobilePricingTiers = () => {
    const [category, setCategory] = useState<'development' | 'growth'>('development');
    const [activePlanIndex, setActivePlanIndex] = useState(1); // Default to middle/second plan

    // Reset index when category changes to ensure valid range
    const handleCategoryChange = (cat: 'development' | 'growth') => {
        setCategory(cat);
        setActivePlanIndex(cat === 'development' ? 1 : 0); // Default to Pro or SEO
    };

    const currentPlans = plans[category];
    const activePlan = currentPlans[activePlanIndex];
    const currentFeatures = featuresData[category];

    return (
        <section className="py-20 px-4 bg-slate-50 relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none" />

            {/* Header */}
            <div className="text-center mb-8 relative z-10">
                <h2 className="text-4xl font-black text-slate-900 mb-2 tracking-tight">
                    Choose Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-violet-600">Ambition</span>
                </h2>
                <p className="text-sm text-slate-500 font-medium">
                    Scalable solutions for your journey.
                </p>
            </div>

            {/* Category Switcher */}
            <div className="flex justify-center mb-8 relative z-10">
                <div className="bg-white p-1 rounded-2xl border border-slate-200 shadow-sm flex relative">
                    {/* Sliding Background */}
                    <motion.div
                        className="absolute top-1 bottom-1 rounded-xl bg-slate-900 shadow-md z-0"
                        initial={false}
                        animate={{
                            x: category === 'development' ? 0 : '100%',
                            width: '50%'
                        }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />

                    <button
                        onClick={() => handleCategoryChange('development')}
                        className={`relative z-10 px-6 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors duration-200 w-1/2 text-center whitespace-nowrap ${category === 'development' ? 'text-white' : 'text-slate-500'}`}
                    >
                        Web & App
                    </button>
                    <button
                        onClick={() => handleCategoryChange('growth')}
                        className={`relative z-10 px-6 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors duration-200 w-1/2 text-center whitespace-nowrap ${category === 'growth' ? 'text-white' : 'text-slate-500'}`}
                    >
                        Growth
                    </button>
                </div>
            </div>

            {/* Plan Tabs Controller */}
            <div className="mb-6 overflow-x-auto hide-scrollbar flex justify-center relative z-10">
                <div className="flex gap-2 p-1 bg-slate-200/50 backdrop-blur-sm rounded-xl">
                    {currentPlans.map((plan, index) => {
                        const isActive = index === activePlanIndex;
                        return (
                            <button
                                key={plan.id}
                                onClick={() => setActivePlanIndex(index)}
                                className={`relative px-4 py-2 rounded-lg text-xs font-bold transition-all duration-300 ${isActive ? 'text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                {isActive && (
                                    <motion.div
                                        layoutId="activePlanTab"
                                        className="absolute inset-0 bg-white rounded-lg shadow-sm"
                                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                    />
                                )}
                                <span className="relative z-10">{plan.name}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Active Pricing Card */}
            <div className="mb-8 relative z-10">
                <AnimatePresence mode='wait'>
                    <motion.div
                        key={activePlan.id}
                        initial={{ opacity: 0, y: 10, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.98 }}
                        transition={{ type: "spring", duration: 0.5, bounce: 0.2 }}
                        className="bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden relative"
                    >
                        {/* Popular Badge */}
                        {activePlan.popular && (
                            <div className="absolute top-0 right-0 bg-gradient-to-bl from-indigo-500 to-violet-600 text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl z-20">
                                POPULAR
                            </div>
                        )}

                        <div className="p-8 pb-6">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="text-2xl font-black text-slate-900">{activePlan.name}</h3>
                                    <p className="text-sm text-indigo-500 font-bold">{activePlan.subtitle}</p>
                                </div>
                                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${activePlan.gradient} flex items-center justify-center text-white shadow-md`}>
                                    <Icon name={(activePlan as any).icon} className="w-6 h-6" />
                                </div>
                            </div>

                            <div className="mb-6">
                                <span className="text-xs text-slate-400 font-bold uppercase tracking-wider block mb-1">Starting from</span>
                                <div className={`text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r ${activePlan.gradient}`}>
                                    {activePlan.price}
                                </div>
                            </div>

                            <p className="text-slate-600 text-sm leading-relaxed mb-6 border-b border-slate-100 pb-6">
                                {activePlan.desc}
                            </p>

                            <Link
                                href={activePlan.price === 'Contact Us' ? "/contact" : "/contact#contact-methods"}
                                className={`w-full py-3.5 rounded-xl font-bold transition-all duration-300 flex items-center justify-center text-sm uppercase tracking-wide ${activePlan.btnStyle}`}
                            >
                                {activePlan.price === 'Contact Us' ? 'Contact Sales' : 'Get Started'}
                            </Link>
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Smart Comparison List */}
            <div className="bg-white rounded-3xl border border-slate-100 shadow-lg p-6 relative z-10">
                <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
                    <Icon name="QueueListIcon" className="w-5 h-5 text-indigo-500" />
                    What's Included
                </h3>

                <div className="space-y-4">
                    {currentFeatures.map((feature, i) => {
                        // Logic to determine if feature is included for current plan
                        // Copied complexity from original file logic:
                        // val = (feature as any)[plan.id] ?? ...
                        const planId = activePlan.id;
                        const included = (feature as any)[planId] ?? (feature as any)[planId.toLowerCase()];

                        return (
                            <div key={i} className="flex items-center justify-between group">
                                <span className={`text-sm font-medium transition-colors ${included ? 'text-slate-700' : 'text-slate-400'}`}>
                                    {feature.name}
                                </span>
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${included ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-50 text-slate-300'}`}>
                                    {included ? (
                                        <Icon name="CheckIcon" className="w-3.5 h-3.5" />
                                    ) : (
                                        <Icon name="XMarkIcon" className="w-3.5 h-3.5" />
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default MobilePricingTiers;
